import React, { useState } from 'react';
import { Zap, Coins, Search, Clock, MapPin, Users, Calendar, Star, Menu, X, GraduationCap } from 'lucide-react';
import { TabbedShowcase, Tab } from './components/TabbedShowcase';
import './index.css';
const showcaseTabs: Tab[] = [{
  id: 'condition-search',
  label: 'Condition Search',
  title: 'Find the Right Appointment Instantly',
  description: 'Search for a single condition and instantly see which clinicians can handle it, along with recommended appointment times and helpful notes for booking staff.',
  image: 'https://cdn.magicpatterns.com/uploads/v25NxgoSda3SAdaxWjH7M3/Result_-_1_Condition.png',
  badges: [{
    text: 'Easy to use',
    color: 'teal'
  }, {
    text: 'Time-saving',
    color: 'blue'
  }, {
    text: 'Best fit matching',
    color: 'green'
  }]
}, {
  id: 'multiple-conditions',
  label: 'Multiple Conditions',
  title: 'Handle Complex Patient Needs',
  description: 'When patients have multiple conditions, Noris finds clinicians qualified to handle all of them in a single appointment, with combined notes and adjusted appointment lengths.',
  image: 'https://cdn.magicpatterns.com/uploads/h6ub5SFC8uMa2JSdcgfWkB/Result_-_2_Conditions.png',
  badges: [{
    text: 'Multi-condition',
    color: 'purple'
  }, {
    text: 'Comprehensive care',
    color: 'teal'
  }, {
    text: 'Efficient',
    color: 'green'
  }]
}];
export function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  return <div className="min-h-screen w-full bg-white font-sans text-gray-900 overflow-x-hidden" data-id="element-0">
      {/* Header */}
      <header className="w-full max-w-[1728px] mx-auto px-6 md:px-[75px] py-[18px] flex items-center justify-between bg-white relative z-50" data-id="element-1">
        <div className="flex-shrink-0" data-id="element-2">
          <img src="https://cdn.magicpatterns.com/uploads/msJLKApK3nKnZMALsRg6Xr/Logo2BName_AptOpt(1).svg" alt="Noris - Appointment Optimiser" className="h-11 w-auto" data-id="element-3" />
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-id="element-4">
          <a href="#features" className="text-[13.6px] font-medium text-gray-600 hover:text-gray-900" data-id="element-5">
            Features
          </a>
          <a href="#product" className="text-[13.6px] font-medium text-gray-600 hover:text-gray-900" data-id="element-6">
            Product
          </a>
          <a href="#testimonials" className="text-[13.6px] font-medium text-gray-600 hover:text-gray-900" data-id="element-7">
            Testimonials
          </a>
          <a href="#contact" className="text-[13.6px] font-medium text-gray-600 hover:text-gray-900" data-id="element-8">
            Contact
          </a>
          <a href="https://calendar.app.google/B7Vep2F5Du23rZYk8" target="_blank" rel="noopener noreferrer" className="bg-[#0088a9] text-white px-6 py-2 rounded-md font-semibold text-[13.6px] hover:bg-[#007795] transition-colors" data-id="element-9">
            Book a Demo
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-10">
          {isMobileMenuOpen ? <X data-id="element-11" /> : <Menu data-id="element-12" />}
        </button>

        {/* Mobile Nav */}
        {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-white shadow-lg p-4 flex flex-col gap-4 md:hidden border-t" data-id="element-13">
            <a href="#features" className="text-sm font-medium text-gray-600" data-id="element-14">
              Features
            </a>
            <a href="#product" className="text-sm font-medium text-gray-600" data-id="element-15">
              Product
            </a>
            <a href="#testimonials" className="text-sm font-medium text-gray-600" data-id="element-16">
              Testimonials
            </a>
            <a href="#contact" className="text-sm font-medium text-gray-600" data-id="element-17">
              Contact
            </a>
            <a href="https://calendar.app.google/B7Vep2F5Du23rZYk8" className="bg-[#0088a9] text-white px-4 py-2 rounded-md font-semibold text-center" data-id="element-18">
              Book a Demo
            </a>
          </div>}
      </header>

      {/* Hero Section */}
      <section className="w-full max-w-[1728px] mx-auto bg-gradient-to-b from-[#e6f7f7] to-white pt-10 pb-20 px-6 md:px-0" data-id="element-19">
        <div className="max-w-[1200px] mx-auto relative flex flex-col lg:flex-row items-center lg:items-start lg:h-[470px]" data-id="element-20">
          {/* Left Content */}
          <div className="w-full lg:w-[520px] z-10 flex flex-col pt-0 lg:pt-0" data-id="element-21">
            <h1 className="text-[40.8px] leading-[48px] font-bold text-gray-900 mb-6" data-id="element-22">
              Find the Most <br data-id="element-23" />
              <span className="text-[#0088a9]" data-id="element-24">
                Cost-Effective Clinician
              </span>{' '}
              <br data-id="element-25" />
              For Every Appointment
            </h1>

            <p className="text-[17px] leading-[28px] text-gray-600 mb-8 max-w-[470px]" data-id="element-26">
              Noris instantly shows you which clinician - whether GP, nurse, or
              HCA - can best handle each patient's needs, helping you optimize
              your practice's resources and reduce unnecessary costs.
            </p>

            {/* Pills */}
            <div className="flex flex-wrap gap-4 mb-8" data-id="element-27">
              <div className="flex items-center bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-100" data-id="element-28">
                <Coins className="w-5 h-5 text-black mr-3" data-id="element-29" />
                <span className="text-[13.6px] font-semibold text-gray-900" data-id="element-30">
                  From 5p per patient
                </span>
              </div>
              <div className="flex items-center bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-100" data-id="element-31">
                <Zap className="w-5 h-5 text-black mr-3" data-id="element-32" />
                <span className="text-[13.6px] font-semibold text-gray-900" data-id="element-33">
                  Quick & easy setup
                </span>
              </div>
            </div>

            <a href="https://calendar.app.google/B7Vep2F5Du23rZYk8" target="_blank" rel="noopener noreferrer" className="bg-[#0088a9] text-white w-fit px-8 py-4 rounded-md font-semibold text-[20px] hover:bg-[#007795] transition-colors shadow-sm" data-id="element-34">
              Book a Demo
            </a>
          </div>

          {/* Right Image */}
          <div className="mt-10 lg:mt-[62px] lg:absolute lg:right-0 lg:top-0 w-full lg:w-[520px] xl:w-[620px] 2xl:w-[700px] h-auto" data-id="element-35">
            <div className="relative" data-id="element-36">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 relative z-10" data-id="element-37">
                <img src="https://framerusercontent.com/images/COpZT9I3SWZddWQpUT0wS0dC0s.png?width=1323&height=733" alt="Noris Dashboard" className="w-full h-auto object-cover" data-id="element-38" />
              </div>
              {/* Decorative Circle */}
              <div className="absolute -bottom-10 -right-10 w-24 h-24 xl:w-28 xl:h-28 bg-[#ccefff] rounded-full -z-0 hidden lg:block" data-id="element-39"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="w-full max-w-[1728px] mx-auto py-16 px-6 md:px-0" data-id="element-40">
        <div className="max-w-[1200px] mx-auto" data-id="element-41">
          {/* Divider */}
          <div className="w-full max-w-[917px] mx-auto h-[3px] bg-gray-900 rounded-full mb-12 opacity-10" data-id="element-42"></div>

          <div className="text-center mb-16" data-id="element-43">
            <h2 className="text-[30.6px] font-bold text-gray-900 mb-4" data-id="element-44">
              Streamline GP Appointment Booking
            </h2>
            <p className="text-[17px] text-gray-600 max-w-3xl mx-auto" data-id="element-45">
              Noris helps booking staff allocate appointments in the most
              efficient way, improving patient care and reducing administrative
              burden.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr" data-id="element-46">
            {/* Feature 1 */}
            <div className="bg-white p-6 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.15)] border border-gray-50 flex flex-col h-full" data-id="element-47">
              <div className="w-12 h-12 bg-[#ccefff] rounded-full flex items-center justify-center mb-6" data-id="element-48">
                <Search className="w-6 h-6 text-black" data-id="element-49" />
              </div>
              <h3 className="text-[17px] font-semibold text-gray-900 mb-2" data-id="element-50">
                Smart Condition Search
              </h3>
              <p className="text-[13.6px] text-gray-600" data-id="element-51">
                Quickly search for appointments based on patient conditions,
                ensuring the right care at the right time.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-6 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.15)] border border-gray-50 flex flex-col h-full" data-id="element-52">
              <div className="w-12 h-12 bg-[#ccefff] rounded-full flex items-center justify-center mb-6" data-id="element-53">
                <Clock className="w-6 h-6 text-black" data-id="element-54" />
              </div>
              <h3 className="text-[17px] font-semibold text-gray-900 mb-2" data-id="element-55">
                Time Optimisation
              </h3>
              <p className="text-[13.6px] text-gray-600" data-id="element-56">
                Automatically suggest optimal appointment length based on
                practice process and patient needs.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-6 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.15)] border border-gray-50 flex flex-col h-full" data-id="element-57">
              <div className="w-12 h-12 bg-[#ccefff] rounded-full flex items-center justify-center mb-6" data-id="element-58">
                <MapPin className="w-6 h-6 text-black" data-id="element-59" />
              </div>
              <h3 className="text-[17px] font-semibold text-gray-900 mb-2" data-id="element-60">
                Multi-Location Support
              </h3>
              <p className="text-[13.6px] text-gray-600" data-id="element-61">
                Seamlessly manage appointments across multiple surgery locations
                from a single interface.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white p-6 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.15)] border border-gray-50 flex flex-col h-full" data-id="element-62">
              <div className="w-12 h-12 bg-[#ccefff] rounded-full flex items-center justify-center mb-6" data-id="element-63">
                <Users className="w-6 h-6 text-black" data-id="element-64" />
              </div>
              <h3 className="text-[17px] font-semibold text-gray-900 mb-2" data-id="element-65">
                Clinician Matching
              </h3>
              <p className="text-[13.6px] text-gray-600" data-id="element-66">
                Match patients with the most appropriate healthcare professional
                based on their conditions.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white p-6 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.15)] border border-gray-50 flex flex-col h-full" data-id="element-67">
              <div className="w-12 h-12 bg-[#ccefff] rounded-full flex items-center justify-center mb-6" data-id="element-68">
                <Calendar className="w-6 h-6 text-black" data-id="element-69" />
              </div>
              <h3 className="text-[17px] font-semibold text-gray-900 mb-2" data-id="element-70">
                Efficient Scheduling
              </h3>
              <p className="text-[13.6px] text-gray-600" data-id="element-71">
                Reduce administrative time with intelligent scheduling that
                optimises the entire booking process.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white p-6 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.15)] border border-gray-50 flex flex-col h-full" data-id="element-72">
              <div className="w-12 h-12 bg-[#ccefff] rounded-full flex items-center justify-center mb-6" data-id="element-73">
                <GraduationCap className="w-6 h-6 text-black" data-id="element-74" />
              </div>
              <h3 className="text-[17px] font-semibold text-gray-900 mb-2" data-id="element-75">
                Accelerated Training
              </h3>
              <p className="text-[13.6px] text-gray-600" data-id="element-76">
                Bring your new team members up to speed immediately, with your
                practices' processes at their fingertips.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Section - Replaced with TabbedShowcase */}
      <div id="product" data-id="element-77">
        <TabbedShowcase tabs={showcaseTabs} sectionTitle="Intelligent Practice Management" sectionDescription="Our intuitive interface makes appointment allocation simple, efficient, and accurate across your entire practice." accentColor="teal" data-id="element-78" />
      </div>

      {/* Testimonials Section */}
      <section id="testimonials" className="w-full max-w-[1728px] mx-auto py-20 px-6 md:px-0 bg-white" data-id="element-79">
        <div className="max-w-[1200px] mx-auto" data-id="element-80">
          <h2 className="text-[30.6px] font-bold text-gray-900 mb-2 text-center" data-id="element-81">
            What Our Users Say
          </h2>
          <p className="text-[17px] text-gray-600 text-center mb-16" data-id="element-82">
            Trusted by GP surgeries across the UK
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-83">
            {/* Testimonial 1 */}
            <div className="bg-gray-50 p-8 rounded-xl" data-id="element-84">
              <div className="flex gap-1 mb-6" data-id="element-85">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-86" />)}
              </div>
              <p className="text-[13.6px] leading-[24px] text-gray-600 mb-8" data-id="element-87">
                "Our team have found the Optimiser very useful. It is more user
                friendly than other tools that we have previously used and
                enables our team to have better knowledge of our clinicians
                skills and the time necessary for each appointment. We are able
                to keep the information current and up to date which, in a fast
                moving organisation, is vital for accuracy."
              </p>
              <div data-id="element-88">
                <p className="text-[13.6px] font-semibold text-gray-900" data-id="element-89">
                  Practice Manager
                </p>
                <p className="text-[12px] text-gray-500" data-id="element-90">Langford Surgery</p>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-gray-50 p-8 rounded-xl flex flex-col justify-between" data-id="element-91">
              <div data-id="element-92">
                <div className="flex gap-1 mb-6" data-id="element-93">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-94" />)}
                </div>
                <p className="text-[13.6px] leading-[24px] text-gray-600 mb-8" data-id="element-95">
                  "We think its fab! The team use it all the time and find it
                  really easy and quick to use and very useful."
                </p>
              </div>
              <div data-id="element-96">
                <p className="text-[13.6px] font-semibold text-gray-900" data-id="element-97">
                  Practice Manager
                </p>
                <p className="text-[12px] text-gray-500" data-id="element-98">St George's Surgery</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="w-full max-w-[1728px] mx-auto relative overflow-hidden" data-id="element-99">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0088a9] to-[#2563eb]" data-id="element-100"></div>
        <div className="relative z-10 max-w-[1200px] mx-auto py-20 px-6 md:px-0 flex flex-col items-center" data-id="element-101">
          <h2 className="text-[30.6px] font-bold text-white mb-4 text-center" data-id="element-102">
            Ready to Transform Your Appointment System?
          </h2>
          <p className="text-[17px] text-[#e6f7f7] text-center mb-12 max-w-2xl" data-id="element-103">
            Join the growing number of GP surgeries using Noris to streamline
            their appointment booking process.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-[800px]" data-id="element-104">
            {/* Card 1 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 flex flex-col items-center text-center" data-id="element-105">
              <h3 className="text-[20.4px] font-semibold text-white mb-4" data-id="element-106">
                Request a Demo
              </h3>
              <p className="text-[13.6px] text-[#e6f7f7] mb-8" data-id="element-107">
                See Noris in action with a personalised demonstration tailored
                to your surgery's needs.
              </p>
              <a href="https://calendar.app.google/B7Vep2F5Du23rZYk8" target="_blank" rel="noopener noreferrer" className="bg-white text-[#0088a9] px-8 py-3 rounded-md font-semibold text-[13.6px] hover:bg-gray-100 transition-colors w-full md:w-auto" data-id="element-108">
                Book a Demo
              </a>
            </div>

            {/* Card 2 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 flex flex-col items-center text-center" data-id="element-109">
              <h3 className="text-[20.4px] font-semibold text-white mb-4" data-id="element-110">
                Contact Our Team
              </h3>
              <p className="text-[13.6px] text-[#e6f7f7] mb-8" data-id="element-111">
                Have questions? Our healthcare technology specialists are here
                to help.
              </p>
              <a href="mailto:tom@noris.uk" className="bg-white text-[#0088a9] px-8 py-3 rounded-md font-semibold text-[13.6px] hover:bg-gray-100 transition-colors w-full md:w-auto" data-id="element-112">
                Get in Touch
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full max-w-[1728px] mx-auto bg-gray-900 py-8 px-6 md:px-[75px]" data-id="element-113">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-114">
          <div className="flex-shrink-0" data-id="element-115">
            <img src="https://cdn.magicpatterns.com/uploads/msJLKApK3nKnZMALsRg6Xr/Logo2BName_AptOpt(1).svg" alt="Noris - Appointment Optimiser" className="h-8 w-auto brightness-0 invert" data-id="element-116" />
          </div>

          <div className="flex items-center gap-4 text-[12px] text-gray-400" data-id="element-117">
            <span data-id="element-118">© 2025 Noris. All rights reserved.</span>
            <span className="text-gray-600" data-id="element-119">|</span>
            <a href="/privacy-policy" className="hover:text-white transition-colors" data-id="element-120">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-121">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>;
}